import { useState, useEffect, useRef } from 'react';
import {
  getAccount, searchAccountsByEmail,
  suspendAccount, unsuspendAccount, banAccount, warnAccount,
  AccountDetail, AdminApiError,
} from '../api/admin-client';
import { useRole } from '../auth/role-context';
import ConfirmDialog from '../components/ConfirmDialog';

type ActionKey = 'warn' | 'suspend' | 'unsuspend' | 'ban' | null;

const RECENT_KEY = 'admin_recent_searches';
const MAX_RECENT = 8;

function statusBadge(account: AccountDetail) {
  if (account.banned_at) return <span className="badge badge-red">Banned</span>;
  if (account.suspended_until) {
    const until = new Date(account.suspended_until);
    if (until > new Date()) {
      return <span className="badge badge-amber">Suspended until {until.toLocaleString()}</span>;
    }
  }
  return <span className="badge badge-green">Active</span>;
}

function roleBadge(role?: string) {
  if (!role || role === 'none') return <span className="badge badge-gray">player</span>;
  if (role === 'super_admin') return <span className="badge badge-red">{role}</span>;
  if (role === 'admin') return <span className="badge badge-blue">{role}</span>;
  return <span className="badge badge-amber">{role}</span>;
}

function timeAgo(iso?: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Detect whether a string looks like an email address */
function looksLikeEmail(s: string): boolean {
  return s.includes('@');
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i}><div className="skeleton-cell" /></td>
      ))}
    </tr>
  );
}

function AccountSkeleton() {
  return (
    <div className="card">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="skeleton-cell" style={{ width: 160, height: 20 }} />
        <div className="skeleton-cell" style={{ width: 60, height: 20 }} />
      </div>
      <table style={{ width: 'auto', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
        <tbody>
          {[120, 200, 100, 60, 60].map((w, i) => (
            <tr key={i}>
              <td style={{ paddingRight: 24 }}><div className="skeleton-cell" style={{ width: 80, height: 14 }} /></td>
              <td><div className="skeleton-cell" style={{ width: w, height: 14 }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Error boundary ───────────────────────────────────────────────────────────

import { Component, ErrorInfo, ReactNode } from 'react';

class AccountErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AccountLookup] Unhandled render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-banner">
          Unexpected render error: {this.state.errorMessage}.
          <button
            className="btn btn-sm btn-ghost"
            style={{ marginLeft: 12 }}
            onClick={() => this.setState({ hasError: false, errorMessage: '' })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Recent search history ────────────────────────────────────────────────────

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveRecent(query: string) {
  const prev = loadRecent().filter(q => q !== query);
  const next = [query, ...prev].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AccountLookup() {
  const { isSuperAdmin } = useRole();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [account, setAccount] = useState<AccountDetail | null>(null);
  const [emailResults, setEmailResults] = useState<AccountDetail[]>([]);

  // Dialog state
  const [action, setAction] = useState<ActionKey>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  // Recent search history
  const [recentSearches, setRecentSearches] = useState<string[]>(loadRecent);
  const [showRecent, setShowRecent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowRecent(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleLookup(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setShowRecent(false);
    setLoading(true);
    setError('');
    setAccount(null);
    setEmailResults([]);

    try {
      if (looksLikeEmail(q)) {
        const results = await searchAccountsByEmail(q);
        if (results.length === 1) {
          setAccount(results[0]);
          saveRecent(q);
          setRecentSearches(loadRecent());
        } else if (results.length > 1) {
          setEmailResults(results);
          saveRecent(q);
          setRecentSearches(loadRecent());
        } else {
          setError(`No accounts found for email: ${q}`);
        }
      } else {
        const result = await getAccount(q);
        setAccount(result);
        saveRecent(q);
        setRecentSearches(loadRecent());
      }
    } catch (err: any) {
      if (err instanceof AdminApiError && err.status === 404) {
        setError(`Account not found: ${q}`);
      } else if (err instanceof AdminApiError && (err.status === 400 || err.status === 422)) {
        setError(`Email search not yet supported by backend (Sprint 24.1 TODO). Try searching by account_id UUID instead.`);
      } else {
        setError(err?.message ?? 'Unknown error');
      }
    } finally {
      setLoading(false);
    }
  }

  function selectRecent(q: string) {
    setQuery(q);
    setShowRecent(false);
    inputRef.current?.focus();
  }

  function clearRecent() {
    localStorage.removeItem(RECENT_KEY);
    setRecentSearches([]);
    setShowRecent(false);
  }

  function openAction(a: ActionKey) {
    setAction(a);
    setActionError('');
    if (a === 'suspend') setFieldValues({ reason: '', duration_hours: '24' });
    else setFieldValues({ reason: '' });
  }

  async function executeAction() {
    if (!account || !action) return;
    const reason = fieldValues.reason ?? '';
    setActionLoading(true);
    setActionError('');
    try {
      if (action === 'warn') {
        await warnAccount(account.account_id, reason);
      } else if (action === 'suspend') {
        const hours = parseInt(fieldValues.duration_hours ?? '24', 10);
        await suspendAccount(account.account_id, reason, hours);
      } else if (action === 'unsuspend') {
        await unsuspendAccount(account.account_id, reason);
      } else if (action === 'ban') {
        await banAccount(account.account_id, reason);
      }
      const refreshed = await getAccount(account.account_id);
      setAccount(refreshed);
      setAction(null);
    } catch (err: any) {
      setActionError(err?.message ?? 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  const isSuspended = account?.suspended_until && new Date(account.suspended_until) > new Date();
  const isBanned = !!account?.banned_at;

  return (
    <AccountErrorBoundary>
      <div>
        <div className="page-title">Account Lookup</div>

        {/* Search + recent history dropdown */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <form className="search-row" onSubmit={handleLookup} style={{ marginBottom: 0 }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="account_id (UUID) or email address"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => recentSearches.length > 0 && setShowRecent(true)}
            />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : null}
              Lookup
            </button>
          </form>

          {/* Recent searches dropdown */}
          {showRecent && recentSearches.length > 0 && (
            <div
              ref={dropdownRef}
              className="recent-dropdown"
            >
              <div className="recent-dropdown-header">
                <span>Recent searches</span>
                <button className="btn btn-sm btn-ghost" onClick={clearRecent} style={{ padding: '2px 6px', fontSize: 11 }}>
                  Clear
                </button>
              </div>
              {recentSearches.map(q => (
                <button
                  key={q}
                  className="recent-dropdown-item"
                  onClick={() => selectRecent(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Empty state — no query yet */}
        {!loading && !error && !account && emailResults.length === 0 && query === '' && (
          <div className="empty-state" style={{ paddingTop: 64 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Search to find an account
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Enter an account_id (UUID) or email address above
            </div>
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        {/* Loading skeleton */}
        {loading && <AccountSkeleton />}

        {/* Multi-account email results */}
        {!loading && emailResults.length > 1 && (
          <div className="card">
            <div className="card-title">Multiple accounts found — select one</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Account ID</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {emailResults.map(a => (
                    <tr key={a.account_id}>
                      <td className="text-mono" style={{ fontSize: 11 }}>{a.account_id}</td>
                      <td>{a.email ?? '—'}</td>
                      <td>{statusBadge(a)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => { setAccount(a); setEmailResults([]); }}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && account && (
          <div className="card">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                {account.username ?? account.email ?? account.account_id}
              </div>
              {statusBadge(account)}
              {roleBadge(account.admin_role)}
            </div>

            <table style={{ width: 'auto', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
              <tbody>
                <tr>
                  <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Account ID</td>
                  <td className="text-mono">{account.account_id}</td>
                </tr>
                {account.email && (
                  <tr>
                    <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Email</td>
                    <td>{account.email}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Created</td>
                  <td>{account.created_at ? new Date(account.created_at).toLocaleString() : '—'}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Sessions</td>
                  <td>{account.session_count ?? '—'}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Reports (against)</td>
                  <td>{account.report_count ?? '—'}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Warnings</td>
                  <td>{account.warn_count ?? 0}</td>
                </tr>
                {isBanned && (
                  <tr>
                    <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Banned at</td>
                    <td className="status-error">{account.banned_at ? new Date(account.banned_at).toLocaleString() : '—'}</td>
                  </tr>
                )}
                {isSuspended && (
                  <tr>
                    <td style={{ color: 'var(--text-secondary)', paddingRight: 24, fontSize: 12 }}>Suspended until</td>
                    <td className="status-warn">
                      {account.suspended_until ? new Date(account.suspended_until).toLocaleString() : '—'}
                      {account.suspension_reason && ` — ${account.suspension_reason}`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Warn history */}
            {account.warn_history && account.warn_history.length > 0 && (
              <div className="mt-16">
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Warn History
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Age</th>
                        <th>Reason</th>
                        <th>Actor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {account.warn_history.map((w, i) => (
                        <tr key={w.warn_id ?? i}>
                          <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{timeAgo(w.created_at)}</td>
                          <td>{w.reason}</td>
                          <td className="text-mono text-muted">{w.actor_account_id ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action cluster */}
            {!isBanned && (
              <div className="action-cluster">
                <button className="btn" onClick={() => openAction('warn')}>Warn</button>
                {!isSuspended && (
                  <button className="btn btn-warning" onClick={() => openAction('suspend')}>Suspend</button>
                )}
                {isSuspended && (
                  <button className="btn" onClick={() => openAction('unsuspend')}>Unsuspend</button>
                )}
                {isSuperAdmin && (
                  <button className="btn btn-danger" onClick={() => openAction('ban')}>Permanent Ban</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Warn dialog */}
        <ConfirmDialog
          open={action === 'warn'}
          title="Issue Warning"
          description={`Issue a warning to account ${account?.account_id}. The player will receive a system notification.`}
          confirmLabel="Issue Warning"
          variant="warning"
          loading={actionLoading}
          fields={[{ key: 'reason', label: 'Reason', type: 'textarea', placeholder: 'Describe the violation...', required: true }]}
          fieldValues={fieldValues}
          onFieldChange={(k, v) => setFieldValues(fv => ({ ...fv, [k]: v }))}
          onConfirm={executeAction}
          onCancel={() => setAction(null)}
        />

        {/* Suspend dialog */}
        <ConfirmDialog
          open={action === 'suspend'}
          title="Suspend Account"
          description={`Suspend account ${account?.account_id}. All current sessions will be revoked.`}
          confirmLabel="Suspend"
          variant="warning"
          loading={actionLoading}
          fields={[
            { key: 'reason', label: 'Reason', type: 'textarea', placeholder: 'Reason for suspension...', required: true },
            { key: 'duration_hours', label: 'Duration (hours)', type: 'number', defaultValue: 24 },
          ]}
          fieldValues={fieldValues}
          onFieldChange={(k, v) => setFieldValues(fv => ({ ...fv, [k]: v }))}
          onConfirm={executeAction}
          onCancel={() => setAction(null)}
        />

        {/* Unsuspend dialog */}
        <ConfirmDialog
          open={action === 'unsuspend'}
          title="Unsuspend Account"
          description={`Clear the suspension on account ${account?.account_id}. The player will need to log in again.`}
          confirmLabel="Unsuspend"
          variant="default"
          loading={actionLoading}
          fields={[{ key: 'reason', label: 'Note (optional)', type: 'text', placeholder: 'e.g. appeal approved' }]}
          fieldValues={fieldValues}
          onFieldChange={(k, v) => setFieldValues(fv => ({ ...fv, [k]: v }))}
          onConfirm={executeAction}
          onCancel={() => setAction(null)}
        />

        {/* Ban dialog */}
        <ConfirmDialog
          open={action === 'ban'}
          title="Permanent Ban"
          description={`Permanently ban account ${account?.account_id}. This requires super_admin role and cannot be undone from this UI.`}
          confirmLabel="Permanently Ban"
          variant="danger"
          loading={actionLoading}
          fields={[{ key: 'reason', label: 'Reason', type: 'textarea', placeholder: 'Reason for permanent ban...', required: true }]}
          fieldValues={fieldValues}
          onFieldChange={(k, v) => setFieldValues(fv => ({ ...fv, [k]: v }))}
          onConfirm={executeAction}
          onCancel={() => setAction(null)}
        />

        {actionError && (
          <div className="error-banner mt-8">{actionError}</div>
        )}

        <style>{`
          .skeleton-cell {
            background: var(--bg-elevated);
            border-radius: var(--radius-sm);
            height: 16px;
            width: 100%;
            animation: skeleton-pulse 1.4s ease-in-out infinite;
          }
          @keyframes skeleton-pulse {
            0%, 100% { opacity: 0.6; }
            50%       { opacity: 1; }
          }
          .recent-dropdown {
            position: absolute;
            top: calc(100% + 4px);
            left: 0;
            right: 0;
            background: var(--bg-surface);
            border: 1px solid var(--border);
            border-radius: var(--radius-md);
            box-shadow: 0 8px 24px rgba(0,0,0,0.3);
            z-index: 200;
            overflow: hidden;
          }
          .recent-dropdown-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px 6px;
            font-size: 11px;
            color: var(--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-weight: 600;
            border-bottom: 1px solid var(--border);
          }
          .recent-dropdown-item {
            display: block;
            width: 100%;
            text-align: left;
            padding: 8px 12px;
            background: transparent;
            border: none;
            border-radius: 0;
            color: var(--text-primary);
            font-size: 13px;
            cursor: pointer;
          }
          .recent-dropdown-item:hover {
            background: var(--bg-hover);
          }
        `}</style>
      </div>
    </AccountErrorBoundary>
  );
}
